/**
 * MultiAgentIdePanel — the dev-agent IDE, replicated VERBATIM for the multi-agent.
 *
 * Same colours, same layout, same components and behaviour as pages/dev-agent
 * (DevAgent.tsx): a persistent left sidebar (metadata explorer + rules), the
 * full MetadataEditorPanel (save / validate / compile / compare / run-test, via
 * the agentic-platform backend), and the RightPanels (dependencies + tests).
 *
 * The ONLY difference from the dev-agent is the centre feed: instead of the
 * dev-agent AgentFeed, the multi-agent ChatPanel renders the multi-step-agentic
 * run. Everything else is the exact dev-agent component, reused — not rebuilt.
 */
import { useEffect, useRef, useState } from "react";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { ErrorBoundary } from "@/components/organisms/ErrorBoundary";
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { ChatPanel } from "@/components/organisms/multi-agent";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { MultiAgentRightPanels } from "@/pages/multi-agent/ide/panels/MultiAgentRightPanels";
import { MultiAgentSidebarSessions } from "@/pages/multi-agent/ide/MultiAgentSidebarSessions";
import { SidebarMetadataSection } from "@/pages/dev-agent/components/SidebarMetadataSection";
import { SidebarRules } from "@/pages/dev-agent/components/SidebarRules";
import { useSidebarMetadata } from "@/pages/dev-agent/components/useSidebarMetadata";
import { MultiAgentMetadataEditorPanel } from "@/pages/multi-agent/ide/editor/MultiAgentMetadataEditorPanel";
import { useMetadataEditorState } from "@/pages/dev-agent/components/useMetadataEditorState";
import { useResizeDrag } from "@/pages/dev-agent/useResizeDrag";
import { DevAgentResizeGrip } from "@/pages/dev-agent/components/DevAgentResizeGrip";
import { MultiAgentEditorWorkspacePinActions } from "@/pages/multi-agent/ide/editor/MultiAgentEditorWorkspacePinActions";
import { runMultiAgentPostSaveAnalysisFlow } from "@/pages/multi-agent/ide/multiAgentPostSaveAnalysis";
import { useMultiAgentEditorBridge } from "@/pages/multi-agent/ide/useMultiAgentEditorBridge";
import { sendMultiAgentMessage } from "@/pages/multi-agent/ide/sendMultiAgentMessage";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import { layoutConfig, type MultiAgentLayoutVariant } from "@/pages/multi-agent/ide/layoutVariant";

export function MultiAgentIdePanel({
  variant,
  composer,
}: { variant?: MultiAgentLayoutVariant; composer?: React.ReactNode } = {}) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  // Layout-only variant config (which panels start collapsed, chat width, editor
  // side). It changes ARRANGEMENT only — every panel/component below is reused
  // unchanged, so chat / editor / agent behaviour is identical across variants.
  const cfg = layoutConfig(variant);

  // MA-3: runStatus is the MULTI-AGENT run status (mirrored into the IDE store),
  // never the dev-agent session.
  const runStatus = useMultiAgentIdeStore((s) => s.runStatus);
  const runningTest = useMultiAgentIdeStore((s) => s.runningTest);

  // ── Layout state (variant-driven, distinct at rest) ──────────────────
  // Each side panel has three modes: full (resizable column), rail (40px icon
  // strip) or hidden (off-canvas, revealed by a floating chevron). `collapsed`
  // tracks rail-vs-full; `hidden` tracks whether it's rendered at all.
  const [sidebarHidden, setSidebarHidden] = useState(cfg.sidebar === "hidden");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(cfg.sidebar !== "full");
  // Left panel shows Files OR Sessions as tabs (not stacked) — matches the
  // workspace layout and keeps the two from crowding each other.
  const [leftTab, setLeftTab] = useState<"files" | "sessions">("files");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rightHidden, setRightHidden] = useState(cfg.right === "hidden");
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(cfg.right !== "full");
  // MA-6: editor open/close is owned by this panel (local state), not the
  // dev-agent store.
  const [editorOpen, setEditorOpen] = useState(false);
  const sidebar = useResizeDrag({ min: 180, max: 400, defaultValue: 240 });
  const editorPanel = useResizeDrag({ min: 350, max: 900, defaultValue: 500 });
  const rightPanel = useResizeDrag({ min: 200, max: 500, defaultValue: cfg.rightWidth, reverse: true });

  // ── Sidebar metadata (reused, org-scoped) ───────────────────────────
  const [selectedMetadata, setSelectedMetadata] = useState<{ id: string; name: string } | null>({ id: "ApexClass", name: "ApexClass" });
  const [isQuickOpenOpen, setIsQuickOpenOpen] = useState(false);
  const { metadataTypeOptions, metadataFiles, loadingFiles, loadingMoreFiles, metadataSearchTerm, hasNextPage, handleLoadMore } = useSidebarMetadata(selectedMetadata);

  // ── Editor state (reused tab/state hook — no dev-agent session) ──────
  // setPendingFile is a no-op here: the multi-agent refactor target is owned by
  // useMultiAgentIdeStore.pendingRefactorFile, not a dev-agent pending file.
  const editor = useMetadataEditorState(() => {});
  const {
    analyzingFile,
    setAnalyzingFile,
    mergedInitialContent,
    handleOpenFileInEditor,
    handleCloseEditor,
    handleLoadIntoChat,
  } = useMultiAgentEditorBridge({
    editor,
    editorOpen,
    setEditorOpen,
    setSidebarCollapsed,
  });

  // MA-14: track the active editor file's bare class name in the IDE store so
  // the coverage consumers (runTest + test SSE) can light the Monaco gutter for
  // the file the user is looking at.
  const activeFileName = editor.openFiles.find((f) => f.id === editor.activeFileId)?.name;
  useEffect(() => {
    useMultiAgentIdeStore.getState().setActiveEditorClassName(bareClassName(activeFileName));
  }, [activeFileName]);

  // New session / session switch (nonce bumped by the New-session + session
  // open/delete actions): close the editor's open files — they belong to the
  // PRIOR session, and leaving them open makes the metadata query keep firing at
  // a dead id, 404-ing ("Metadata item not found") during the transition.
  const newSessionNonce = useMultiAgentIdeStore((s) => s.newSessionNonce);
  const closeEditorRef = useRef(editor.handleCloseMetadataEditor);
  closeEditorRef.current = editor.handleCloseMetadataEditor;
  const firstNonceRef = useRef(true);
  useEffect(() => {
    if (firstNonceRef.current) { firstNonceRef.current = false; return; }
    closeEditorRef.current();
  }, [newSessionNonce]);

  /** Sidebar file open: preview on a single click, permanent on a double click. */
  const openSidebarFile = (
    file: { id: string | number; name: string; metadataType?: string; content?: string },
    { preview }: { preview: boolean },
  ) => {
    useMultiAgentIdeStore.getState().setPendingRefactorFile({
      id: String(file.id),
      name: file.name,
      content: file.content || "",
      metadataType: file.metadataType,
    });
    handleOpenFileInEditor(
      { id: String(file.id), name: file.name, metadataType: file.metadataType },
      { preview },
    );
  };

  // MA-4 + MA-5: send a message to the MULTI-AGENT run. `displayText` is shown in
  // the chat bubble while the full prompt is sent as the backend user_intent.
  const handleSendMessage = (message: string, displayText?: string) => {
    void sendMultiAgentMessage(message, displayText);
  };

  // MA-15: when the agent produces a proposed artifact derived from a source
  // (`artifact_created.metadata.source_artifact_id`), open it as a tab showing
  // the refactored code and stash the original so the user can review it — but
  // do NOT auto-open the diff. The diff opens ONLY when the user clicks Compare
  // in the editor toolbar (per product decision: never auto-compare).
  // MA-17: badge "C" (created/new) when there is no source artifact id.
  const artifacts = useMultiAgentStore((s) => s.artifacts);
  const sourceArtifactIds = useMultiAgentIdeStore((s) => s.sourceArtifactIds);
  const handledCompareArtifactsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // Find the newest artifact carrying a source link that we haven't opened yet.
    const candidate = [...artifacts]
      .reverse()
      .find((a) => sourceArtifactIds[a.id] && !handledCompareArtifactsRef.current.has(a.id));
    if (!candidate) return;
    handledCompareArtifactsRef.current.add(candidate.id);
    void (async () => {
      try {
        const { getArtifact } = await import("@/services/multi-agent");
        const art = await getArtifact(candidate.id);
        let proposed = art.content || "";
        if (!proposed) return;
        // Refactor *diff artifacts* carry their before/after INSIDE the content
        // as JSON ({fixes:[{source:{original,replacement,unified_diff}}]}), and
        // their source_artifact_id points at the blast-radius JSON — NOT the
        // original class. Parse it so Compare shows real apex (original vs
        // replacement) instead of raw JSON diffed against a grep result.
        let diffOriginal: string | null = null;
        let isDeleteFix = false;
        if (proposed.trimStart().startsWith("{")) {
          try {
            const parsed = JSON.parse(proposed) as {
              fixes?: Array<{
                kind?: string;
                source?: { original?: string; replacement?: string };
              }>;
            };
            // A delete fix has replacement === "" BY DESIGN (no new body) — it
            // must still match here, or it falls through as "not a diff-json
            // artifact" and the raw JSON envelope gets shown as if it were code.
            const fix = parsed.fixes?.find(
              (f) => f?.source?.replacement || f?.kind === "delete",
            );
            if (fix) {
              isDeleteFix = fix.kind === "delete";
              diffOriginal = fix.source?.original ?? "";
              // The tab's baseline content: a delete has no new body to show as
              // "the file", so show the ORIGINAL (matches codeActions.ts).
              proposed = isDeleteFix ? diffOriginal : (fix.source?.replacement ?? proposed);
            }
          } catch {
            /* not a diff-json artifact — keep the raw content */
          }
        }
        const metadataType =
          (art.metadata?.metadata_type as string) ||
          (art.language === "trigger" ? "ApexTrigger" : "ApexClass");
        // Open the proposed artifact as a tab showing the refactored code. We
        // do NOT call editor.setAcceptedCode here — that would auto-open the
        // diff. Instead we stash the target + original so the Compare button can
        // open the diff on demand.
        editor.openTab({ id: candidate.id, name: candidate.name, metadataType });
        editor.setInitialContent((prev) => ({ ...prev, [candidate.id]: proposed }));
        setEditorOpen(true);
        const ide = useMultiAgentIdeStore.getState();
        // Record which tab the diff belongs to and the in-hand original (left
        // side) so the manual Compare button shows refactored-vs-original.
        // Deliberately NOT arming acceptedCodeOperation="deleted" here — MA-15
        // fires passively on ANY artifact arrival, before the user has looked
        // at anything. Arming delete-intent from a passive effect means a
        // reflexive Save (no review at all) could delete a file the user never
        // saw. Only the explicit "Review Delete" card action (codeActions.ts)
        // may arm it; this effect just makes sure the tab shows real Apex
        // (the original body) instead of the raw JSON envelope.
        ide.setAcceptedCodeTarget(candidate.id);
        ide.setAcceptedCodeOriginal(diffOriginal);
        // Always set explicitly (never skip) — acceptedCodeTarget just moved to
        // THIS tab, so a stale "deleted" from a prior tab's unresolved review
        // must not silently carry over and arm isDeleteTarget for this one.
        ide.setAcceptedCodeOperation(isDeleteFix ? null : diffOriginal != null ? "modified" : null);
        // MA-17: "C" when there is no original (new file); otherwise it's a diff.
        ide.setArtifactBadge(
          candidate.id,
          diffOriginal != null || sourceArtifactIds[candidate.id] ? "M" : "C",
        );
      } catch (err) {
        console.warn("[multi-agent compare] auto-open failed", err);
      }
    })();
  }, [artifacts, sourceArtifactIds, editor, setEditorOpen]);

  // ── Panel blocks (built once, then ARRANGED per layout variant) ──────
  // Each block is the exact same component/markup as the original single
  // layout; only their order, default-collapsed state, sizing and the chat
  // width differ between variants. Nothing functional changes.
  const sidebarBlock = sidebarHidden ? null : !sidebarCollapsed ? (
    <div
      className="ma-left-utility min-w-[180px] max-w-[400px] flex flex-col overflow-hidden shrink-0 min-h-0 h-full"
      style={{ width: sidebar.width }}
    >
      {/* Tabs: Files | Sessions — shown one at a time so they never crowd. */}
      <div className="ma-panel-toolbar flex shrink-0 items-center gap-1 px-2 py-1 z-10">
        <button
          onClick={() => setLeftTab("files")}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${leftTab === "files" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}
        >
          Files{metadataFiles.length ? ` · ${metadataFiles.length}` : ""}
        </button>
        <button
          onClick={() => setLeftTab("sessions")}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${leftTab === "sessions" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}
        >
          Sessions
        </button>
        <button onClick={() => setSidebarCollapsed(true)} className="ml-auto p-1 rounded hover:bg-muted/50 transition-colors" title="Collapse sidebar">
          <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="ma-panel-section ma-panel-section--grow flex-1 min-h-0 overflow-y-auto text-sm">
        {leftTab === "files" ? (
          <SidebarMetadataSection
            metadataTypeOptions={metadataTypeOptions} selectedMetadata={selectedMetadata} setSelectedMetadata={setSelectedMetadata}
            metadataFiles={metadataFiles} loadingFiles={loadingFiles} loadingMoreFiles={loadingMoreFiles}
            metadataSearchTerm={metadataSearchTerm} hasNextPage={hasNextPage} isQuickOpenOpen={isQuickOpenOpen}
            setIsQuickOpenOpen={setIsQuickOpenOpen} onLoadMore={handleLoadMore}
            // Single click previews (one tab, reused); double click keeps the tab.
            // Both mark the class as the refactor target: its UUID + name + source
            // is seeded into the next multi-agent run so the agent refactors that
            // exact class, and that is true however the file was opened.
            onOpenFileInEditor={(file) => openSidebarFile(file, { preview: true })}
            onOpenFilePersistent={(file) => openSidebarFile(file, { preview: false })}
            // Mirrors the editor: the row for the active tab is highlighted, so the
            // sidebar and the tab strip never disagree about which file you are on.
            activeFileId={editor.activeFileId}
            disabled={runStatus === "running"}
          />
        ) : (
          <MultiAgentSidebarSessions />
        )}
      </div>
      {/* Rules — stacked below the files, COLLAPSED by default so Files/Sessions
          get the full column height; expands on demand, never eats the panel. */}
      <div className="ma-panel-section shrink-0 border-t border-border/40">
        <button
          type="button"
          onClick={() => setRulesOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-foreground/85 transition-colors hover:bg-muted/30"
        >
          {rulesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Rules
        </button>
        {rulesOpen && (
          <div className="max-h-[30vh] overflow-y-auto pb-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full">
            <SidebarRules userId={useAuthStoreV1.getState().user?.id || ""} />
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="w-10 border-r border-border/50 flex flex-col items-center pt-2 shrink-0">
      <button onClick={() => setSidebarCollapsed(false)} className="p-1.5 rounded hover:bg-muted/50 transition-colors" title="Expand sidebar">
        <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
      </button>
    </div>
  );

  const sidebarGrip = !sidebarHidden && !sidebarCollapsed ? (
    <DevAgentResizeGrip onMouseDown={sidebar.onMouseDown} title="Drag to resize sidebar" />
  ) : null;

  // Code Editor — the real dev-agent editor (save / compare / run-test).
  const editorEl = editorOpen && editor.openFiles.length > 0 ? (
    <ErrorBoundary fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Editor crashed — close and reopen the file</div>}>
      <MultiAgentMetadataEditorPanel
        openFiles={editor.openFiles} activeFileId={editor.activeFileId}
        // Activation only — routing this through openTab would silently promote a
        // preview tab just because the user looked at it.
        onActiveFileChange={(id) => id && editor.activateTab(id)}
        onCloseTab={editor.handleCloseTab} onReorderTabs={editor.reorderTabs}
        onPromoteTab={editor.promoteTab} panelWidth={editorPanel.width}
        onClose={handleCloseEditor} acceptedCode={editor.acceptedCode}
        onAcceptedCodeChange={(code) => editor.setAcceptedCode(code)} onPendingFileChange={() => {}}
        initialContent={mergedInitialContent} orgId={orgId} onLoadIntoChat={handleLoadIntoChat}
        isAnalyzing={!!analyzingFile}
        onCanonicalFileId={(canonicalId, previousId) => { editor.migrateFileId(previousId, canonicalId); }}
        onFileSaved={async (data) => {
          editor.setInitialContent((prev) => {
            const next = { ...prev };
            delete next[data.id];
            return next;
          });
          // MA-1: post-save analysis runs against the MULTI-AGENT run, not
          // the dev-agent session. Results arrive via SSE (MA-7); the
          // synchronous body is a fast-path. No run => nothing to analyze.
          const runId = useMultiAgentStore.getState().runId;
          if (!runId) return;
          setAnalyzingFile(data.fileName);
          try {
            await runMultiAgentPostSaveAnalysisFlow(runId, data);
          } catch (err) {
            console.warn("[PostSaveAnalysis] failed:", err);
          } finally {
            setAnalyzingFile(null);
          }
        }}
        extraActions={
          <MultiAgentEditorWorkspacePinActions
            activeFile={editor.openFiles.find((f) => f.id === editor.activeFileId)}
            initialContentById={editor.initialContent}
            orgId={orgId}
            busy={runStatus === "running"}
          />
        }
        onRunTest={(className) => {
          // runTest owns ALL feedback (start announce + error cases + results) —
          // a call-site toast here double-announced alongside the store's
          // (2de2e1ae's call-site toast superseded by the centralized one).
          void useMultiAgentIdeStore.getState().runTest(className);
        }}
        isBusy={runStatus === "running"}
        isTestRunning={!!runningTest}
      />
    </ErrorBoundary>
  ) : null;

  const editorGrip = editorEl ? (
    <DevAgentResizeGrip onMouseDown={editorPanel.onMouseDown} title="Drag to resize editor" />
  ) : null;

  // Centre feed — the MULTI-AGENT chat. `--ma-prose-max` controls the reading
  // width: "100%" lets prose fill the column; a fixed value (Focus variant)
  // centers it into a comfortable reading measure.
  const proseMax =
    cfg.chat === "narrow" ? "720px" : cfg.chat === "wide" ? "960px" : "100%";
  const chatBlock = (
    <div
      className="ma-center-workspace relative z-[2] flex flex-col min-h-0 h-full min-w-0 flex-1"
      style={{ ["--ma-prose-max" as string]: proseMax } as React.CSSProperties}
    >
      <div className="flex min-w-0 min-h-0 flex-1">
        <ChatPanel hideRail />
      </div>
      {composer != null && <div className="shrink-0">{composer}</div>}
    </div>
  );

  // Rightmost — ONE consolidated panel (Run details + Dependencies + Tests)
  // reading the MULTI-AGENT session via useMultiAgentIdeStore.
  const rightGrip = !rightHidden && !rightPanelCollapsed ? (
    <DevAgentResizeGrip onMouseDown={rightPanel.onMouseDown} title="Drag to resize right panel" />
  ) : null;
  const rightBlock = rightHidden ? null : (
    <MultiAgentRightPanels
      onSendMessage={handleSendMessage}
      onOpenFileInEditor={handleOpenFileInEditor}
      rightPanelWidth={rightPanel.width}
      analyzingFile={analyzingFile}
      isBusy={runStatus === "running"}
      collapsed={rightPanelCollapsed}
      onToggleCollapse={() => setRightPanelCollapsed((v) => !v)}
    />
  );

  // Split layouts keep the editor pane ALWAYS visible to the RIGHT of the chat
  // (a placeholder until a file is opened) so the two-column split reads as the
  // layout. On-demand layouts dock the editor to the LEFT of the chat, and only
  // once a file is actually open (classic behaviour).
  const splitPlaceholder =
    cfg.editor === "split" && !editorEl ? (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 border-l border-border/50 px-6 text-center text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Code editor</span>
        <span>Open a class from the sidebar to edit &amp; compare it here.</span>
      </div>
    ) : null;

  const centre =
    cfg.editor === "split" ? (
      <>
        {chatBlock}
        {editorGrip}
        {editorEl}
        {splitPlaceholder}
      </>
    ) : (
      // On-demand: editor docks to the LEFT of the chat (next to the meta panel),
      // matching the intended layout — was rendering on the right (opposite).
      <>
        {editorEl}
        {editorGrip}
        {chatBlock}
      </>
    );

  // Floating reveal chevrons for off-canvas (hidden) panels — the chrome-free
  // layouts (focus / zen / split) can still bring a panel back on demand.
  const sidebarReveal = sidebarHidden ? (
    <button
      type="button"
      onClick={() => { setSidebarHidden(false); setSidebarCollapsed(true); }}
      title="Show sidebar"
      className="absolute left-1 top-1 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      <PanelLeftOpen className="h-4 w-4" />
    </button>
  ) : null;
  const rightReveal = rightHidden ? (
    <button
      type="button"
      onClick={() => { setRightHidden(false); setRightPanelCollapsed(true); }}
      title="Show run details"
      className="absolute right-1 top-1 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      <PanelRightOpen className="h-4 w-4" />
    </button>
  ) : null;

  return (
    <div className="ma-ide-shell relative flex flex-1 min-h-0 h-full w-full overflow-hidden">
      <div className="ma-ide-body relative flex min-h-0 flex-1 overflow-hidden">
        {sidebarReveal}
        {sidebarBlock}
        {sidebarGrip}
        {centre}
        {rightGrip}
        {rightBlock}
        {rightReveal}
      </div>
    </div>
  );
}
